import React, { Suspense, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { GrowthBookProvider } from '@growthbook/growthbook-react'
import i18n from 'i18next'

import { MetaInfo } from 'components/MetaInfo'
import { Spinner } from 'components/Spinner'
import { TranslationKeysModal } from 'components/TranslationKeysModal'

import { selectAppConfig } from 'root-redux/selects/common'

import { useAppNameFromUrl } from 'hooks/useAppNameFromUrl'
import { useGiaFlowTrackerInitialization } from 'hooks/useGiaFlowTrackerInitialization'
import { growthBook } from 'hooks/useGrowthbook'
import { useInitAppConfig } from 'hooks/useInitAppConfig'
import { useLinkSourceFromUrl } from 'hooks/useLinkSourceFromUrl'
import { useScrollFix } from 'hooks/useScrollFix'
import { useUserId } from 'hooks/useUserId'

import { getCurrentEnv } from 'helpers/getCurrentEnv'
import { setViewportFullHeight } from 'helpers/setViewportFullHeight'

import { browserHistory } from 'browser-history'
import { CurrentEnvironment } from 'root-constants'

import { CustomRouter } from './CustomRouter'
import { RouteList } from './RouteList'
import {
  I18nInterceptorProvider,
  i18nInterceptor,
  setI18nInterceptorCallback,
  useI18nInterceptor,
} from './lib/i18n'

setViewportFullHeight()

const AppLayout = ({ children }) => {
  const { addTranslation } = useI18nInterceptor()
  const isStageEnvironment = getCurrentEnv() === CurrentEnvironment.STAGE

  useEffect(() => {
    if (isStageEnvironment) setI18nInterceptorCallback(addTranslation)
  }, [addTranslation, isStageEnvironment])

  return children
}

export const App: React.FC = () => {
  const config = useSelector(selectAppConfig)

  const isStageEnvironment = getCurrentEnv() === CurrentEnvironment.STAGE

  if (isStageEnvironment) i18nInterceptor(i18n)

  useAppNameFromUrl()
  useLinkSourceFromUrl()
  useUserId()
  useGiaFlowTrackerInitialization()
  useInitAppConfig()
  useScrollFix()

  return (
    <CustomRouter history={browserHistory}>
      <MetaInfo />
      <Suspense fallback={<Spinner />}>
        <GrowthBookProvider growthbook={growthBook}>
          <I18nInterceptorProvider>
            <AppLayout>
              <>
                {config ? <RouteList /> : <Spinner />}
                <TranslationKeysModal />
              </>
            </AppLayout>
          </I18nInterceptorProvider>
        </GrowthBookProvider>
      </Suspense>
    </CustomRouter>
  )
}
